import { Badge, Box, Flex, IconButton, SimpleGrid, Text } from '@chakra-ui/react';
import CustomButton from 'components/CustomButton';
import TableComponent from 'components/TableComponent';
import { queryClient } from 'index';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { FaEye } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import PullToRefresh from 'react-simple-pull-to-refresh';
import { Column } from 'react-table';
import { findCarnets } from 'services/api.service';
import { Carnet } from 'types/Carnet';
import { maskCurrency } from 'utils/number';
import {
  CARNETS_QUERY_KEY,
  carnetInstallmentsLabel,
  carnetPayer,
  carnetSearchTerm,
  carnetsPageSize,
  carnetStatusMeta,
  formatIsoDate,
} from './carnet';

export default function Carnets() {
  const navigate = useNavigate();

  const columns = useMemo(
    () =>
      [
        {
          Header: 'Descrição',
          accessor: 'title',
          Cell: ({ value }: { value?: string | null }) => value || 'Carnê',
        },
        {
          Header: 'Pagador',
          accessor: 'payerName',
          Cell: ({ row }: { row: { original: Carnet } }) => {
            const payer = carnetPayer(row.original);
            if (!payer) return '—';
            return (
              <Box>
                <Text>{payer.name}</Text>
                {payer.document && (
                  <Text fontSize="xs" color="gray.500">
                    {payer.document}
                  </Text>
                )}
              </Box>
            );
          },
        },
        {
          Header: 'Parcelas',
          accessor: 'totalItems',
          Cell: ({ row }: { row: { original: Carnet } }) => carnetInstallmentsLabel(row.original, maskCurrency),
        },
        {
          Header: '1º vencimento',
          accessor: 'metadata.firstDueDate',
          Cell: ({ value }: { value?: string | null }) => formatIsoDate(value),
        },
        {
          Header: 'Status',
          accessor: 'status',
          Cell: ({ value }: { value: string }) => {
            const meta = carnetStatusMeta(value);
            return <Badge colorScheme={meta.colorScheme}>{meta.label}</Badge>;
          },
        },
        {
          Header: 'Emitidos',
          accessor: 'succeededItems',
          Cell: ({ row }: { row: { original: Carnet } }) =>
            `${row.original.succeededItems} de ${row.original.totalItems}`,
        },
        {
          Header: 'Criado em',
          accessor: 'createdAt',
          Cell: ({ value }: { value: string }) => DateTime.fromISO(value).toFormat('dd/MM/yyyy HH:mm'),
        },
        {
          Header: 'Ações',
          accessor: 'actions',
        },
      ] as Column[],
    [],
  );

  const actions = (row: Carnet) => (
    <Flex align="center" justify="flex-start" w="100%">
      <IconButton
        aria-label="Ver carnê"
        size="sm"
        variant="outline"
        icon={<FaEye />}
        onClick={() => navigate(`/access/carnes/${row.id}`)}
      />
    </Flex>
  );

  const handleRefresh = async () => {
    await queryClient.invalidateQueries({
      queryKey: [CARNETS_QUERY_KEY],
    });
  };

  return (
    <PullToRefresh onRefresh={handleRefresh}>
      <Box>
        <SimpleGrid mb="20px" columns={{ sm: 1 }} spacing={{ base: '20px', xl: '20px' }}>
          <TableComponent
            label="Carnês de boletos"
            columnsData={columns}
            right={
              <CustomButton variant="solid" onClick={() => navigate('/access/carnes/novo')}>
                Emitir carnê
              </CustomButton>
            }
            // O "Filtrar" do botão Filtros busca na descrição e no nome/documento do pagador.
            queryFn={(filter: { page: number; limit: number; filter?: string }) =>
              findCarnets({
                page: filter.page,
                limit: carnetsPageSize(filter.limit),
                search: carnetSearchTerm(filter.filter),
              })
            }
            queryKey={CARNETS_QUERY_KEY}
            actions={actions}
          />
        </SimpleGrid>
      </Box>
    </PullToRefresh>
  );
}
